import React from 'react';
import { isNumber } from 'lodash';
import { space, style, fontSize, lineHeight } from 'styled-system';
import {
  styled,
  Text as PrimitiveTextControl,
} from '@staffino/primitive-controls';
import { StyledComponent } from '@emotion/styled';
import {
  breakpoints,
  fontSize as textFontSize,
  spaceBlacklist,
  textAlignBlacklist,
  marginCssResolver,
} from './theme';
import omitProps from './omit-props';

const align = style({
  prop: 'align',
  cssProperty: 'text-align',
});

const bl = [
  'bold',
  'margin',
  'size',
  'weight',
  'align',
  ...spaceBlacklist,
  ...textAlignBlacklist,
  'fontStyle',
  'italic',
  'lineHeight',
  'uppercase',
  'dispatch',
];

const pickSizeProp = (props, index) => {
  if (isNumber(props.size)) {
    return `${props.size}px`;
  }
  if (textFontSize[props.size]) {
    return textFontSize[props.size];
  }
  if (Array.isArray(props.size)) {
    if (isNumber(props.size[index])) {
      return `${props.size}px`;
    }
    if (textFontSize[props.size[index]]) {
      return textFontSize[props.size[index]];
    }
  }
  return textFontSize.normal;
};

const TextWrapper = ({ value, message, children, color, ...props }) => {
  let childrenWithProps = children;
  if (Array.isArray(children)) {
    childrenWithProps = children.map((child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          /* hasTextParent: true, color */
        });
      }
      return child;
    });
  }
  return (
    <PrimitiveTextControl {...props}>
      {value}
      {message || childrenWithProps}
    </PrimitiveTextControl>
  );
};

const Text: StyledComponent<React.ComponentProps<typeof PrimitiveTextControl>> =
  styled(omitProps(bl, TextWrapper))(
    (props) => ({
      lineHeight: props.lineHeight,
      fontFamily: "'Source Sans Pro', sans-serif",
      fontSize: pickSizeProp(props, 0),
      fontWeight: props.weight || (props.bold ? '700' : '400'),
      fontStyle: props.fontStyle || (props.italic ? 'italic' : 'normal'),
      wordBreak: 'break-word',
      textTransform: props.uppercase ? 'uppercase' : 'none',
      color: props.theme.colors[props.color] || props.color,
      [`@media (min-width: ${breakpoints[0]})`]: {
        fontSize: props.size[1]
          ? pickSizeProp(props, 1)
          : pickSizeProp(props, props.size.length - 1),
      },
      [`@media (min-width: ${breakpoints[1]})`]: {
        fontSize: props.size[2]
          ? pickSizeProp(props, 2)
          : pickSizeProp(props, props.size.length - 1),
      },
      [`@media (min-width: ${breakpoints[2]})`]: {
        fontSize: props.size[3]
          ? pickSizeProp(props, 3)
          : pickSizeProp(props, props.size.length - 1),
      },
      [`@media (min-width: ${breakpoints[3]})`]: {
        fontSize: props.size[4]
          ? pickSizeProp(props, 4)
          : pickSizeProp(props, props.size.length - 1),
      },
      ...marginCssResolver(),
    }),
    space,
    align,
    fontSize,
    lineHeight,
  );

Text.defaultProps = {
  textAlign: 'left',
  lineHeight: 1.2,
  size: 'normal',
  color: '',
};

export default Text;
